import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useReportStore } from '@store';
import {
  useDeleteReportFavoriteMutation,
  useDeleteReportMutation,
  useEditUserReportMutation,
  useMakeReportFavoriteMutation,
  useReportQuery,
  useUserIntrospectionQuery,
} from '@common/cache';
import { IColumnDefinition, paths } from '@common/types';
import { IUsersPreviewFilters, IPreviewTableFilters } from '../../types.ts';
import { useHandleSaveReport } from '../../../hooks';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { DropdownMenuItem } from '@components';
import { t } from 'i18next';

const baseFilters: IUsersPreviewFilters = {
  search: '',
  sort_by: 'name',
  sort_order: 'ASC',
  page: 1,
  limit: 100,
  group_by: '',
};

export const useUsersPreview = () => {
  const params = useParams();
  const {
    report,
    setReport,
    setIntrospection,
    filters: reportFilters,
    setIsFormInitialized,
    hideSaveOptions,
    isOwner,
  } = useReportStore();
  const { data } = useUserIntrospectionQuery();
  const { isLoading } = useReportQuery({ report_id: params.report_id });

  const {
    mutateAsync: makeReportFavoriteMutation,
    isPending: makeReportFavoriteIsPending,
  } = useMakeReportFavoriteMutation();

  const { mutateAsync: deleteReportFavoriteMutation } =
    useDeleteReportFavoriteMutation();

  const {
    mutateAsync: deleteReportMutation,
    isPending: deleteReportIsLoading,
  } = useDeleteReportMutation();

  const {
    mutateAsync: makeReportPrivateMutation,
    isPending: privateIsPending,
  } = useEditUserReportMutation(report?.id ?? '');

  useEffect(() => {
    if (data) {
      setIntrospection(data);
    }
  }, [data]);

  //Here we need to initialize the filter values.
  const initialFilters: IUsersPreviewFilters = {
    ...baseFilters,
    sort_by: 'name',
    options: {
      $and: reportFilters?.map(o => ({
        field: o.field,
        operator: o.operator,
        value: o.value,
      })) as paths['/reporting/users/reports/{report_id}']['get']['responses']['200']['content']['application/json']['options']['$and'],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      select: (report as any)?.options?.select || [],
    },
    interval: report?.interval,
    ...(report?.custom_timeframe && {
      custom_timeframe: {
        from_date: report?.custom_timeframe?.from_date ?? '',
        to_date: report?.custom_timeframe?.to_date ?? '',
      },
    }),
  };

  const [filters, setFilters] = useState<IUsersPreviewFilters>(initialFilters);

  useEffect(() => {
    if (report?.interval) {
      setFilters(prev => ({
        ...prev,
        interval: report.interval,
        ...(report.custom_timeframe
          ? {
              custom_timeframe: {
                from_date: report.custom_timeframe.from_date ?? '',
                to_date: report.custom_timeframe.to_date ?? '',
              },
            }
          : { custom_timeframe: undefined }),
      }));
    }
  }, [report?.interval, report?.custom_timeframe]);

  useEffect(() => {
    if (report?.id) {
      setFilters(prev => ({
        ...prev,
        options: {
          ...prev.options,
          $and: reportFilters?.map(o => ({
            field: o.field,
            operator: o.operator,
            value: o.value,
          })) as paths['/reporting/users/reports/{report_id}']['get']['responses']['200']['content']['application/json']['options']['$and'],
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          select: (report as any)?.options?.select || [],
        },
      }));
    }
  }, [report?.id]);

  const [isFiltersCardOpen, setIsFiltersCardOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isReportHistoryModalOpen, setIsReportHistoryModalOpen] =
    useState(false);

  const allPossibleColumns = data?.groups?.flatMap(o => o.fields) ?? [];

  const columns: IColumnDefinition[] =
    report?.options?.select
      .map(rep => {
        const column = allPossibleColumns.find(col => col.field === rep);
        return column;
      })
      .filter(o => o !== undefined)
      .map(o => ({
        id: o.field,
        label: o.label,
        sortable: true,
      })) ?? [];

  const navigate = useNavigate();

  const handleBackToEdit = () => {
    if (report?.id) {
      setIsFormInitialized(true);
      navigate(`/reporting/edit/${report.id}`);
    } else {
      navigate(`/reporting/create`);
    }
  };

  const handleGoToReports = () => {
    navigate('/reporting/reports');
  };

  const handleOpenHistoryModal = () => {
    setIsReportHistoryModalOpen(true);
  };

  const handleCloseHistoryModal = () => {
    setIsReportHistoryModalOpen(false);
  };

  const handleOpenFiltersCard = () => {
    setIsFiltersCardOpen(true);
  };
  const handleCloseFiltersCard = () => {
    setIsFiltersCardOpen(false);
  };

  const handleToggleFiltersCard = () => {
    setIsFiltersCardOpen(!isFiltersCardOpen);
  };

  const handleFilterChange = (newFilters: IPreviewTableFilters) => {
    setFilters({
      ...filters,
      page: 1,
      ...newFilters,
    } as IUsersPreviewFilters);
  };

  const handleClearAll = () => {
    setFilters({
      ...initialFilters,
    } as IUsersPreviewFilters);
  };

  const { handleSaveReport } = useHandleSaveReport();

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleMakeReportFavorite = async () => {
    try {
      await makeReportFavoriteMutation(report?.id || '');
      setReport({ ...report, favorite: true });
      toast.success('Report set as favorite successfully.');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDeleteReportFavorite = async () => {
    try {
      await deleteReportFavoriteMutation(report?.id || '');
      setReport({ ...report, favorite: false });
      toast.success('Report removed from favorite successfully.');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleToggleReportPrivate = async (isPrivate: boolean) => {
    try {
      await makeReportPrivateMutation({ private: isPrivate });
      setReport({ ...report, private: isPrivate });
      toast.success(
        `Report ${isPrivate ? 'set as private' : 'removed from private'} successfully.`,
      );
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDeleteReport = async () => {
    try {
      await deleteReportMutation(report?.id || '');

      toast.success('Report deleted successfully.');
      navigate('/reporting/reports');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      handleCloseDeleteModal();
    }
  };

  const contextValues = {
    filters,
    handleFilterChange,
    isFiltersCardOpen,
    handleOpenFiltersCard,
    handleCloseFiltersCard,
    handleToggleFiltersCard,
    handleClearAll,
  };

  const dropdownMenu: DropdownMenuItem[] = [
    {
      iconName: 'none',
      label: t('reporting.report.preview.setPrivate'),
      onClick: () => handleToggleReportPrivate(true),
      disabled:
        hideSaveOptions || report?.private || privateIsPending || !isOwner,
    },
    {
      iconName: 'none',
      label: t('reporting.report.preview.setFavorite'),
      onClick: () => handleMakeReportFavorite(),
      disabled: report?.favorite || makeReportFavoriteIsPending,
    },
    {
      iconName: 'none',
      label: t('reporting.report.preview.viewHistory'),
      onClick: () => handleOpenHistoryModal(),
    },
    {
      iconName: 'none',
      label: t('reporting.report.preview.delete'),
      onClick: () => setIsDeleteModalOpen(true),
      borderTop: true,
      disabled: hideSaveOptions,
    },
  ];

  return {
    handleBackToEdit,
    handleGoToReports,
    contextValues,
    columns,
    report,
    handleSaveReport,
    hideSaveOptions,
    dropdownMenu,
    previewMode: !params.report_id,
    isDeleteModalOpen,
    handleCloseDeleteModal,
    isDeleteLoading: deleteReportIsLoading,
    handleDeleteReport,
    handleDeleteReportFavorite,
    makeReportFavoriteIsPending,
    handleToggleReportPrivate,
    privateIsPending,
    isReportHistoryModalOpen,
    handleCloseHistoryModal,
    isLoading,
  };
};
